import { ApiFetchError, apiFetch } from "../utils/apiFetch";

export type BereichZustand = { zustand: "ok" | "gestoert"; fehler: string | null; seit: string | null };

export type RemarkableVerbindung = {
    userId: string;
    verbunden: boolean;
    verbundenAm: string;
    anlegen: BereichZustand;
    abholen: BereichZustand;
    letzterAbgleich: string | null;
    letzteAbholung: string | null;
    notizbuecher: { aktiv: number; archiviert: number; nurGeraet: number };
    abgleichLaeuft: boolean;
};

export type RemarkableZustand = { eingerichtet: boolean; verbindungen: RemarkableVerbindung[] };

export type RemarkablePruefung = { ok: boolean; meldung: string; zustand: RemarkableZustand };

const alsJson = (body: unknown): RequestInit => ({
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
});

const pfad = (userId: string) => `remarkable/verbindungen/${encodeURIComponent(userId)}`;

export const remarkableService = {
    zustand: async (): Promise<RemarkableZustand> => (await apiFetch<RemarkableZustand>("remarkable/zustand")).data,
    verbinden: async (userId: string, code: string): Promise<RemarkablePruefung> =>
        (await apiFetch<RemarkablePruefung>("remarkable/verbindungen", alsJson({ userId, code }))).data,
    pruefen: async (userId: string): Promise<RemarkablePruefung> =>
        (await apiFetch<RemarkablePruefung>(`${pfad(userId)}/pruefen`, { method: "POST" })).data,
    abgleichen: async (userId: string): Promise<RemarkablePruefung> =>
        (await apiFetch<RemarkablePruefung>(`${pfad(userId)}/abgleichen`, { method: "POST" })).data,
    trennen: async (userId: string): Promise<RemarkableZustand> =>
        (await apiFetch<RemarkableZustand>(pfad(userId), { method: "DELETE" })).data,
};

const ALLGEMEIN = "Die Anfrage an KMVS ist fehlgeschlagen. Bitte später erneut versuchen.";

/** Statuscodes, bei denen die `message` aus unserem Controller stammt und deutsch ist. */
const EIGENE_TEXTE = new Set([400, 404, 409, 502]);

/**
 * Text für den Nutzer zu einem fehlgeschlagenen Aufruf.
 *
 * 401/403 kommen vom AuthzGuard und tragen englische Technik-Meldungen
 * („Missing required role“) – die bekommen feste deutsche Sätze. Nur bei den
 * Statuscodes, die unser Controller selbst mit deutschem Text wirft, wird dessen
 * `message` durchgereicht; alles andere (500, Proxy-Fehler, kein JSON) bleibt
 * der allgemeine Satz.
 */
export function remarkableFehlertext(e: unknown): string {
    if (!(e instanceof ApiFetchError)) return ALLGEMEIN;
    if (e.status === 401) return "Die Anmeldung ist abgelaufen. Bitte neu anmelden.";
    if (e.status === 403) return "Nur Administratoren dürfen reMarkable-Verbindungen verwalten.";
    if (EIGENE_TEXTE.has(e.status)) {
        try {
            const m = (JSON.parse(e.message.replace(/^HTTP \d+: /, "")) as { message?: unknown } | null)?.message;
            if (typeof m === "string" && m.trim()) return m;
        } catch {
            /* keine JSON-Antwort */
        }
    }
    return ALLGEMEIN;
}
